import { notify } from "./api";
import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { Editor } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Sparkles } from "lucide-react";
import {
  AGENT_MENTION_LABEL,
  filterMentionPeople,
  mentionQuery,
  mentionsIn,
  type Mention,
} from "../shared/mentions";
import type { Person } from "./ReviewControls";
import "./mentions.css";
/** The agent row: first, when the query could still spell "Agent". */
const AGENT = { id: "", name: AGENT_MENTION_LABEL, role: "agent" } as Person;
function choicesFor(people: Person[], query: string, agent: boolean) {
  const matchesAgent =
    agent &&
    AGENT_MENTION_LABEL.toLocaleLowerCase().startsWith(
      query.trim().toLocaleLowerCase(),
    );
  return [...(matchesAgent ? [AGENT] : []), ...filterMentionPeople(people, query)];
}
export function MentionPicker({
  rich,
  people,
  agent = false,
  error,
  onRetry,
  onInsert,
}: {
  rich: Editor | null;
  people: Person[];
  agent?: boolean;
  error?: string;
  onRetry?: () => void;
  onInsert?: (mention: Mention) => void;
}) {
  const id = useId(),
    [match, setMatch] = useState<{
      from: number;
      to: number;
      query: string;
      x: number;
      y: number;
    } | null>(null),
    [active, setActive] = useState(0);
  const dismissed = useRef(""),
    current = useRef({ match, active, people, agent, onInsert });
  current.current = { match, active, people, agent, onInsert };
  const choose = (person: Person) => {
    const m = current.current.match;
    if (!rich || !m) return;
    if (person === AGENT) {
      rich
        .chain()
        .focus()
        .insertContentAt({ from: m.from, to: m.to }, [
          {
            type: "agentMention",
            attrs: { occurrenceId: crypto.randomUUID() },
          },
          { type: "text", text: " " },
        ])
        .run();
      setMatch(null);
      return;
    }
    const tokens = mentionsIn(rich.getJSON());
    if (
      tokens.length >= 100 ||
      (!tokens.some((m) => m.userId === person.id) &&
        new Set(tokens.map((m) => m.userId)).size >= 20)
    ) {
      notify("Choose up to 20 project members per message.");
      return;
    }
    const mention = {
      userId: person.id,
      label: person.name.slice(0, 120),
      occurrenceId: crypto.randomUUID(),
    };
    rich
      .chain()
      .focus()
      .insertContentAt({ from: m.from, to: m.to }, [
        { type: "mention", attrs: mention },
        { type: "text", text: " " },
      ])
      .run();
    onInsert?.(mention);
    setMatch(null);
  };
  const chooseRef = useRef(choose);
  chooseRef.current = choose;
  useEffect(() => {
    if (!rich) return;
    const update = () => {
      if (
        rich.isDestroyed ||
        !rich.isFocused ||
        !rich.state.selection.empty ||
        rich.isActive("link")
      )
        return setMatch(null);
      const { $from, from } = rich.state.selection;
      const q = mentionQuery(
        $from.parent.textBetween(0, $from.parentOffset, "\n", "\ufffc"),
      );
      if (!q) {
        dismissed.current = "";
        return setMatch(null);
      }
      const key = `${from}:${q.query}`;
      if (dismissed.current === key) return;
      const rect = rich.view.coordsAtPos(from);
      setMatch((old) => {
        if (old?.query !== q.query) setActive(0);
        return {
          from: from - q.length,
          to: from,
          query: q.query,
          x: rect.left,
          y: rect.bottom,
        };
      });
    };
    const key = new PluginKey("calque-mention-picker");
    rich.registerPlugin(
      new Plugin({
        key,
        props: {
          handleKeyDown: (_view, e) => {
            const s = current.current;
            if (!s.match || e.isComposing) return false;
            const choices = choicesFor(s.people, s.match.query, s.agent);
            if (e.key === "Escape") {
              dismissed.current = `${s.match.to}:${s.match.query}`;
              setMatch(null);
              e.stopPropagation();
              return true;
            }
            if (
              choices.length &&
              ["ArrowDown", "ArrowUp", "Enter"].includes(e.key)
            ) {
              e.stopPropagation();
              if (e.key === "Enter")
                chooseRef.current(
                  choices[Math.min(s.active, choices.length - 1)],
                );
              else
                setActive(
                  (i) =>
                    (i + (e.key === "ArrowDown" ? 1 : -1) + choices.length) %
                    choices.length,
                );
              return true;
            }
            return false;
          },
        },
      }),
      (plugin, plugins) => [plugin, ...plugins],
    );
    rich.on("selectionUpdate", update);
    rich.on("update", update);
    rich.on("focus", update);
    const blur = () => setMatch(null);
    rich.on("blur", blur);
    update();
    return () => {
      rich.off("selectionUpdate", update);
      rich.off("update", update);
      rich.off("focus", update);
      rich.off("blur", blur);
      if (!rich.isDestroyed) rich.unregisterPlugin(key);
    };
  }, [rich]);
  const choices = choicesFor(people, match?.query ?? "", agent);
  const members = choices.filter((p) => p !== AGENT).length;
  useEffect(() => {
    document
      .getElementById(`${id}-${active}`)
      ?.scrollIntoView({ block: "nearest" });
  }, [active, id]);
  useEffect(() => {
    if (!rich || rich.isDestroyed) return;
    const el = rich.view.dom;
    if (match) {
      el.setAttribute("aria-controls", id);
      el.setAttribute("aria-expanded", "true");
      if (choices.length)
        el.setAttribute(
          "aria-activedescendant",
          `${id}-${Math.min(active, choices.length - 1)}`,
        );
      else el.removeAttribute("aria-activedescendant");
    } else {
      el.removeAttribute("aria-controls");
      el.removeAttribute("aria-expanded");
      el.removeAttribute("aria-activedescendant");
    }
    return () => {
      el.removeAttribute("aria-controls");
      el.removeAttribute("aria-expanded");
      el.removeAttribute("aria-activedescendant");
    };
  }, [rich, match, active, choices.length, id]);
  if (!match) return null;
  return createPortal(
    <div
      className="mention-picker"
      style={{
        left: Math.max(8, Math.min(innerWidth - 268, match.x)),
        top: Math.max(8, Math.min(innerHeight - 270, match.y + 6)),
      }}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.preventDefault()}
    >
      <small aria-live="polite">
        {error
          ? "People unavailable"
          : members
            ? `${members} project ${members === 1 ? "member" : "members"}`
            : choices.length
              ? "Ask the agent to work on this"
              : "No matching members"}
      </small>
      {error && (
        <button type="button" onClick={onRetry}>
          Retry
        </button>
      )}
      <div role="listbox" id={id} aria-label="Mention someone">
        {choices.map((p, i) => (
          <button
            id={`${id}-${i}`}
            type="button"
            role="option"
            aria-selected={i === active}
            key={p === AGENT ? "agent" : p.id}
            className={p === AGENT ? "mention-agent" : undefined}
            onMouseEnter={() => setActive(i)}
            onClick={() => choose(p)}
          >
            <span className="mention-avatar">
              {p === AGENT ? (
                <Sparkles size={13} aria-hidden="true" />
              ) : (
                p.name.slice(0, 1).toUpperCase()
              )}
            </span>
            <span>
              {p.name}
              {p === AGENT && <small>Works on what this comment is pinned to</small>}
              {p !== AGENT && people.some(
                (other) => other.id !== p.id && other.name === p.name,
              ) && (
                <small>
                  {p.role} · {p.id.slice(-6)}
                </small>
              )}
            </span>
          </button>
        ))}
      </div>
    </div>,
    document.body,
  );
}
